// src/services/matrizCurricularService.js
import api from './api';

const matrizCurricularService = {
  // ============================================
  // MATRIZ CURRICULAR
  // ============================================

  /**
   * Listar todas as matrizes curriculares com filtros
   * @param {Object} params - { page, limit, search, idCurso, ano, ativa }
   */
  list: async (params = {}) => {
    try {
      const response = await api.get('/matriz-curricular', { params });
      return response.data;
    } catch (error) {
      console.error('Erro ao listar matrizes:', error);
      if (error.response?.data) {
        throw new Error(error.response.data.message || 'Erro ao buscar matrizes');
      }
      throw new Error('Erro de conexão. Tente novamente.');
    }
  },

  /**
   * Buscar matriz por ID (dados básicos)
   * @param {number} id - ID da matriz
   */
  getById: async (id) => {
    try {
      const response = await api.get(`/matriz-curricular/${id}`);
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar matriz:', error);
      if (error.response?.data) {
        throw new Error(error.response.data.message || 'Erro ao buscar matriz');
      }
      throw new Error('Erro de conexão. Tente novamente.');
    }
  },

  /**
   * Buscar matriz por ID com módulos e disciplinas
   * @param {number} id - ID da matriz
   */
  getByIdWithModulos: async (id) => {
    try {
      const response = await api.get(`/matriz-curricular/${id}`, {
        params: { includeModulos: true }
      });
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar matriz completa:', error);
      if (error.response?.data) {
        throw new Error(error.response.data.message || 'Erro ao buscar matriz completa');
      }
      throw new Error('Erro de conexão. Tente novamente.');
    }
  },

  /**
   * Criar nova matriz curricular
   * @param {Object} matrizData - { idCurso, ano, nome, vigenciaInicio, vigenciaFim, observacoes }
   */
  create: async (matrizData) => {
    try {
      const response = await api.post('/matriz-curricular', matrizData);
      return response.data;
    } catch (error) {
      console.error('Erro ao criar matriz:', error);
      if (error.response?.data) {
        throw new Error(error.response.data.message || 'Erro ao criar matriz');
      }
      throw new Error('Erro de conexão. Tente novamente.');
    }
  },

  /**
   * Atualizar matriz curricular
   * @param {number} id - ID da matriz
   * @param {Object} matrizData - Dados a atualizar
   */
  update: async (id, matrizData) => {
    try {
      const response = await api.put(`/matriz-curricular/${id}`, matrizData);
      return response.data;
    } catch (error) {
      console.error('Erro ao atualizar matriz:', error);
      if (error.response?.data) {
        throw new Error(error.response.data.message || 'Erro ao atualizar matriz');
      }
      throw new Error('Erro de conexão. Tente novamente.');
    }
  },

  /**
   * Deletar matriz curricular
   * @param {number} id - ID da matriz
   */
  delete: async (id) => {
    try {
      const response = await api.delete(`/matriz-curricular/${id}`);
      return response.data;
    } catch (error) {
      console.error('Erro ao deletar matriz:', error);
      if (error.response?.data) {
        throw new Error(error.response.data.message || 'Erro ao deletar matriz');
      }
      throw new Error('Erro de conexão. Tente novamente.');
    }
  },

  /**
   * Ativar/Desativar matriz
   * @param {number} id - ID da matriz
   */
  toggleAtiva: async (id) => {
    try {
      const response = await api.patch(`/matriz-curricular/${id}/toggle-ativa`);
      return response.data;
    } catch (error) {
      console.error('Erro ao alterar status da matriz:', error);
      if (error.response?.data) {
        throw new Error(error.response.data.message || 'Erro ao alterar status');
      }
      throw new Error('Erro de conexão. Tente novamente.');
    }
  },

  // ============================================
  // MÓDULOS
  // ============================================

  /**
   * Listar módulos de uma matriz
   * @param {number} idMatriz - ID da matriz
   */
  listModulos: async (idMatriz) => {
    try {
      const response = await api.get(`/matriz-curricular/${idMatriz}/modulos`);
      return response.data;
    } catch (error) {
      console.error('Erro ao listar módulos:', error);
      if (error.response?.data) {
        throw new Error(error.response.data.message || 'Erro ao buscar módulos');
      }
      throw new Error('Erro de conexão. Tente novamente.');
    }
  },

  /**
   * Buscar módulo por ID com disciplinas
   * @param {number} idModulo - ID do módulo
   */
  getModuloById: async (idModulo) => {
    try {
      const response = await api.get(`/matriz-curricular/modulos/${idModulo}`);
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar módulo:', error);
      if (error.response?.data) {
        throw new Error(error.response.data.message || 'Erro ao buscar módulo');
      }
      throw new Error('Erro de conexão. Tente novamente.');
    }
  },

  /**
   * Criar novo módulo
   * @param {number} idMatriz - ID da matriz
   * @param {Object} moduloData - { nome, descricao, ordem }
   */
  createModulo: async (idMatriz, moduloData) => {
    try {
      const response = await api.post(`/matriz-curricular/${idMatriz}/modulos`, moduloData);
      return response.data;
    } catch (error) {
      console.error('Erro ao criar módulo:', error);
      if (error.response?.data) {
        throw new Error(error.response.data.message || 'Erro ao criar módulo');
      }
      throw new Error('Erro de conexão. Tente novamente.');
    }
  },

  /**
   * Atualizar módulo
   * @param {number} idModulo - ID do módulo
   * @param {Object} moduloData - Dados a atualizar
   */
  updateModulo: async (idModulo, moduloData) => {
    try {
      const response = await api.put(`/matriz-curricular/modulos/${idModulo}`, moduloData);
      return response.data;
    } catch (error) {
      console.error('Erro ao atualizar módulo:', error);
      if (error.response?.data) {
        throw new Error(error.response.data.message || 'Erro ao atualizar módulo');
      }
      throw new Error('Erro de conexão. Tente novamente.');
    }
  },

  /**
   * Deletar módulo
   * @param {number} idModulo - ID do módulo
   */
  deleteModulo: async (idModulo) => {
    try {
      const response = await api.delete(`/matriz-curricular/modulos/${idModulo}`);
      return response.data;
    } catch (error) {
      console.error('Erro ao deletar módulo:', error);
      if (error.response?.data) {
        throw new Error(error.response.data.message || 'Erro ao deletar módulo');
      }
      throw new Error('Erro de conexão. Tente novamente.');
    }
  },

  /**
   * Mover módulo (up/down)
   * @param {number} idModulo - ID do módulo
   * @param {string} direction - 'up' ou 'down'
   */
  moveModulo: async (idModulo, direction) => {
    try {
      const response = await api.post(`/matriz-curricular/modulos/${idModulo}/move`, { direction });
      return response.data;
    } catch (error) {
      console.error('Erro ao mover módulo:', error);
      if (error.response?.data) {
        throw new Error(error.response.data.message || 'Erro ao mover módulo');
      }
      throw new Error('Erro de conexão. Tente novamente.');
    }
  },

  /**
   * Duplicar módulo
   * @param {number} idModulo - ID do módulo
   */
  duplicateModulo: async (idModulo) => {
    try {
      const response = await api.post(`/matriz-curricular/modulos/${idModulo}/duplicate`);
      return response.data;
    } catch (error) {
      console.error('Erro ao duplicar módulo:', error);
      if (error.response?.data) {
        throw new Error(error.response.data.message || 'Erro ao duplicar módulo');
      }
      throw new Error('Erro de conexão. Tente novamente.');
    }
  },

  // ============================================
  // DISCIPLINAS
  // ============================================

  /**
   * Listar disciplinas de um módulo
   * @param {number} idModulo - ID do módulo
   */
  listDisciplinas: async (idModulo) => {
    try {
      const response = await api.get(`/matriz-curricular/modulos/${idModulo}/disciplinas`);
      return response.data;
    } catch (error) {
      console.error('Erro ao listar disciplinas:', error);
      if (error.response?.data) {
        throw new Error(error.response.data.message || 'Erro ao buscar disciplinas');
      }
      throw new Error('Erro de conexão. Tente novamente.');
    }
  },

  /**
   * Listar todas as disciplinas de uma matriz
   * @param {number} idMatriz - ID da matriz
   */
  listDisciplinasByMatriz: async (idMatriz) => {
    try {
      const response = await api.get(`/matriz-curricular/${idMatriz}/disciplinas`);
      return response.data;
    } catch (error) {
      console.error('Erro ao listar disciplinas da matriz:', error);
      if (error.response?.data) {
        throw new Error(error.response.data.message || 'Erro ao buscar disciplinas');
      }
      throw new Error('Erro de conexão. Tente novamente.');
    }
  },

  /**
   * Buscar produtos disponíveis para adicionar como disciplina
   * @param {number} idMatriz - ID da matriz
   */
  getDisciplinasDisponiveis: async (idMatriz) => {
    try {
      const response = await api.get(`/matriz-curricular/${idMatriz}/disciplinas/disponiveis`);
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar disciplinas disponíveis:', error);
      if (error.response?.data) {
        throw new Error(error.response.data.message || 'Erro ao buscar disciplinas disponíveis');
      }
      throw new Error('Erro de conexão. Tente novamente.');
    }
  },

  /**
   * Criar nova disciplina (vincular produto ao módulo)
   * @param {number} idModulo - ID do módulo
   * @param {Object} disciplinaData - { idProduto, obrigatoria, cargaHoraria, ordem }
   */
  createDisciplina: async (idModulo, disciplinaData) => {
    try {
      const response = await api.post(`/matriz-curricular/modulos/${idModulo}/disciplinas`, disciplinaData);
      return response.data;
    } catch (error) {
      console.error('Erro ao adicionar disciplina:', error);
      if (error.response?.data) {
        throw new Error(error.response.data.message || 'Erro ao adicionar disciplina');
      }
      throw new Error('Erro de conexão. Tente novamente.');
    }
  },

  /**
   * Atualizar disciplina
   * @param {number} idDisciplina - ID da disciplina na matriz
   * @param {Object} disciplinaData - Dados a atualizar
   */
  updateDisciplina: async (idDisciplina, disciplinaData) => {
    try {
      const response = await api.put(`/matriz-curricular/disciplinas/${idDisciplina}`, disciplinaData);
      return response.data;
    } catch (error) {
      console.error('Erro ao atualizar disciplina:', error);
      if (error.response?.data) {
        throw new Error(error.response.data.message || 'Erro ao atualizar disciplina');
      }
      throw new Error('Erro de conexão. Tente novamente.');
    }
  },

  /**
   * Deletar disciplina
   * @param {number} idDisciplina - ID da disciplina na matriz
   */
  deleteDisciplina: async (idDisciplina) => {
    try {
      const response = await api.delete(`/matriz-curricular/disciplinas/${idDisciplina}`);
      return response.data;
    } catch (error) {
      console.error('Erro ao remover disciplina:', error);
      if (error.response?.data) {
        throw new Error(error.response.data.message || 'Erro ao remover disciplina');
      }
      throw new Error('Erro de conexão. Tente novamente.');
    }
  },

  /**
   * Mover disciplina (up/down)
   * @param {number} idDisciplina - ID da disciplina
   * @param {string} direction - 'up' ou 'down'
   */
  moveDisciplina: async (idDisciplina, direction) => {
    try {
      const response = await api.post(`/matriz-curricular/disciplinas/${idDisciplina}/move`, { direction });
      return response.data;
    } catch (error) {
      console.error('Erro ao mover disciplina:', error);
      if (error.response?.data) {
        throw new Error(error.response.data.message || 'Erro ao mover disciplina');
      }
      throw new Error('Erro de conexão. Tente novamente.');
    }
  },

  /**
   * Alternar obrigatoriedade da disciplina
   * @param {number} idDisciplina - ID da disciplina
   */
  toggleObrigatoria: async (idDisciplina) => {
    try {
      const response = await api.patch(`/matriz-curricular/disciplinas/${idDisciplina}/toggle-obrigatoria`);
      return response.data;
    } catch (error) {
      console.error('Erro ao alterar obrigatoriedade:', error);
      if (error.response?.data) {
        throw new Error(error.response.data.message || 'Erro ao alterar obrigatoriedade');
      }
      throw new Error('Erro de conexão. Tente novamente.');
    }
  },

  /**
   * Atualizar ordem de múltiplas disciplinas (drag-and-drop)
   * @param {Array} disciplinas - Array de { id, ordem }
   */
  updateOrdensDisciplinas: async (disciplinas) => {
    try {
      const response = await api.put('/matriz-curricular/disciplinas/reordenar', { disciplinas });
      return response.data;
    } catch (error) {
      console.error('Erro ao reordenar disciplinas:', error);
      if (error.response?.data) {
        throw new Error(error.response.data.message || 'Erro ao reordenar disciplinas');
      }
      throw new Error('Erro de conexão. Tente novamente.');
    }
  },

  // ============================================
  // PROGRESSO DO ALUNO
  // ============================================

  /**
   * Buscar progresso do aluno em uma matriz
   * @param {number} idMatriz - ID da matriz
   * @param {number} idAluno - ID do aluno
   * @param {number} idCurso - ID do curso
   */
  getProgressoAluno: async (idMatriz, idAluno, idCurso) => {
    try {
      const response = await api.get(`/matriz-curricular/${idMatriz}/progresso/${idAluno}`, {
        params: { idCurso }
      });
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar progresso do aluno:', error);
      if (error.response?.data) {
        throw new Error(error.response.data.message || 'Erro ao buscar progresso');
      }
      throw new Error('Erro de conexão. Tente novamente.');
    }
  },

  /**
   * Gerar PDF do progresso do aluno
   * @param {number} idMatriz - ID da matriz
   * @param {number} idAluno - ID do aluno
   * @param {number} idCurso - ID do curso
   */
  imprimirProgressoAluno: async (idMatriz, idAluno, idCurso) => {
    try {
      const response = await api.get(`/matriz-curricular/${idMatriz}/progresso/${idAluno}/pdf`, {
        params: { idCurso },
        responseType: 'blob',
        timeout: 120000 // 2 minutos para geração de PDF
      });

      // Criar blob e fazer download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `progresso_aluno_${idAluno}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      return { success: true };
    } catch (error) {
      console.error('Erro ao gerar PDF do progresso:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Erro ao gerar PDF'
      };
    }
  },
};

export default matrizCurricularService;

